import { useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeSlash, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);

    if (formData.new_password !== formData.confirm_password) {
      toast.error('As senhas não coincidem');
      return;
    }
    if (formData.new_password.length < 6) {
      toast.error('Nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: formData.current_password,
        new_password: formData.new_password
      }, { withCredentials: true });
      toast.success('Senha alterada com sucesso!');
      setFormData({ current_password: '', new_password: '', confirm_password: '' });
      setSuccess(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in max-w-lg" data-testid="settings-page">
        <div>
          <h1 className="page-title">Configurações</h1>
          <p className="text-[#73706A] mt-1">Gerenciar sua conta</p>
        </div>

        {/* Profile Info */}
        <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
          <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4">Minha Conta</h2>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-[#73706A]">Nome</p>
              <p className="font-medium text-[#1A1A1A]">{user?.name}</p>
            </div>
            <div>
              <p className="text-sm text-[#73706A]">Email</p>
              <p className="font-medium text-[#1A1A1A]">{user?.email}</p>
            </div>
            <div>
              <p className="text-sm text-[#73706A]">Tipo</p>
              <p className="font-medium text-[#1A1A1A] capitalize">{user?.role}</p>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
          <h2 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
            <Lock size={20} className="text-[#E85D04]" />
            Alterar Senha
          </h2>

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3" data-testid="password-success">
              <CheckCircle size={20} className="text-[#2D6A4F]" weight="fill" />
              <p className="text-sm text-[#2D6A4F]">Senha alterada com sucesso!</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Senha atual *</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={formData.current_password}
                  onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                  placeholder="Digite sua senha atual"
                  required
                  className="pr-12"
                  data-testid="current-password-input"
                />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#73706A] hover:text-[#1A1A1A]">
                  {showCurrent ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <Label>Nova senha *</Label>
              <div className="relative mt-1">
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={formData.new_password}
                  onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                  required
                  className="pr-12"
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#73706A] hover:text-[#1A1A1A]">
                  {showNew ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirmar nova senha *</Label>
              <Input
                type="password"
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                placeholder="Repita a nova senha"
                required
                className="mt-1"
                data-testid="confirm-new-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#E85D04] hover:bg-[#DC2F02] text-white w-full"
              data-testid="change-password-btn"
            >
              {saving ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
